import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { shipmentsApi } from '../../api/shipments-api';
import { NotFoundError } from '../../api/errors';
import { returnInstructions, type Shipment } from '../../domain/shipment';
import { bookingErrorMessage } from '../shipment-create/booking-errors';
import { TransferToCarrierAction } from './TransferToCarrierAction';

/**
 * The Cargonizer label for a shipment koi booked, and for returns the
 * instructions to send the customer with it.
 */
export function ShipmentLabelPanel({ shipment }: { shipment: Shipment }) {
  const [copied, setCopied] = useState(false);
  const label = useMutation({
    mutationFn: () => shipmentsApi.getShipmentLabel(shipment.id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `label-${shipment.cargonizerConsignmentNumber}.pdf`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  if (!shipment.cargonizerConsignmentId) {
    return (
      <section className="site-card" aria-label="Shipment label">
        <div className="site-card-heading">
          <h2>Label</h2>
        </div>
        <p className="empty-note">
          No label is available in koi for this shipment. It was recorded manually or booked before
          labels were stored; find it in Cargonizer by consignment{' '}
          {shipment.cargonizerConsignmentNumber}.
        </p>
      </section>
    );
  }

  const isReturn = shipment.direction === 'RETURN';
  const instructions = isReturn ? returnInstructions(shipment) : '';

  return (
    <section className="site-card" aria-label="Shipment label">
      <div className="site-card-heading">
        <h2>{isReturn ? 'Return label for the customer' : 'Label'}</h2>
      </div>
      {isReturn ? (
        <p>
          Send the label and these instructions to{' '}
          {shipment.origin?.contactName ? `${shipment.origin.contactName} at ` : ''}
          {shipment.origin?.name ?? 'the customer'}.
        </p>
      ) : null}
      <div className="button-group">
        <button type="button" onClick={() => label.mutate()} disabled={label.isPending}>
          {label.isPending ? 'Fetching label…' : 'Download label (PDF)'}
        </button>
        {isReturn ? (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(instructions)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
          >
            {copied ? 'Instructions copied' : 'Copy instructions'}
          </button>
        ) : null}
      </div>
      {label.isError ? (
        <p role="alert" className="form-error">
          {label.error instanceof NotFoundError
            ? 'Cargonizer has no label for this shipment.'
            : bookingErrorMessage(label.error)}
        </p>
      ) : null}
      <TransferToCarrierAction shipment={shipment} />
      {isReturn ? (
        <pre className="return-instructions" aria-label="Return instructions">
          {instructions}
        </pre>
      ) : null}
    </section>
  );
}
